/**
 * THE PUBLICATION-ONLY DISPLAY LAYER (lead ruling 2026-09-26 15:02). Member
 * display names come from the dataset's OWN name fields (GL functions:
 * `function_name` in the onboarding pack's function_hierarchy.csv), held as a
 * versioned data file here. Only publication and narration read it; the reader
 * catalog never does, so no interpretation reading re-draws (the earlier label
 * change went through the catalog and broke readings).
 *
 * Exact-key lookup for the answer's own datasets; a member with no display name
 * keeps its raw label — the scope is never dropped (correctness over polish).
 */
import glAccounts from './display-names/chrw_functional_gl.account.v1.json';
import glFunctions from './display-names/chrw_functional_gl.function.v1.json';
import glSubfunctions from './display-names/chrw_functional_gl.subfunction.v1.json';
import phoenixEliminationFunctions from './display-names/phoenix_integration_eliminations.function.v1.json';
import phoenixEliminationLevels from './display-names/phoenix_integration_eliminations.level.v1.json';

export type AskMemberDisplayNames = Readonly<{
  datasetRef: string;
  dimension: string;
  source: string;
  version: number;
  names: Readonly<Record<string, string>>;
}>;

export const ASK_MEMBER_DISPLAY_NAMES: readonly AskMemberDisplayNames[] = Object.freeze([
  glFunctions as AskMemberDisplayNames,
  glSubfunctions as AskMemberDisplayNames,
  glAccounts as AskMemberDisplayNames,
  phoenixEliminationFunctions as AskMemberDisplayNames,
  phoenixEliminationLevels as AskMemberDisplayNames,
]);

export type AskMemberKey = Readonly<{ datasetRef: string; dimension: string; memberRef: string }>;

const hasName = (map: AskMemberDisplayNames, memberRef: string) =>
  Object.prototype.hasOwnProperty.call(map.names, memberRef);

/** The exact display name for one member of one dimension of one dataset, or null. */
export function governedMemberDisplayName(
  key: AskMemberKey,
  maps: readonly AskMemberDisplayNames[] = ASK_MEMBER_DISPLAY_NAMES
): string | null {
  const map = maps.find(
    (candidate) => candidate.datasetRef === key.datasetRef && candidate.dimension === key.dimension
  );
  return map && hasName(map, key.memberRef) ? map.names[key.memberRef] : null;
}

/**
 * The display label for a scope label whose dimension the caller does not know: it maps only
 * when exactly ONE map of the answer's datasets names it (e.g. a GL function ref that is not
 * also an account code). Ambiguous or unknown labels stay raw. Prefer governedMemberDisplayName.
 */
export function memberDisplayLabel(
  datasetRefs: readonly string[],
  rawLabel: string,
  maps: readonly AskMemberDisplayNames[] = ASK_MEMBER_DISPLAY_NAMES
): string {
  const refs = new Set(datasetRefs);
  const named = maps.filter((map) => refs.has(map.datasetRef) && hasName(map, rawLabel));
  return named.length === 1 ? named[0].names[rawLabel] : rawLabel;
}
